// TODO: Set default value of the picker to selectedDate.
// Right now it just defaults to today each reload.

import React, { useCallback, useEffect, useState } from "react";
import { Select, useId } from "@fluentui/react-components";
import { CalendarLtr20Regular } from "@fluentui/react-icons";
import dayjs, { Dayjs } from "dayjs";
import {
  abandonCartDialogContent,
  abandonCartDialogTitle,
  appInsightsEvents,
  trackEvent,
  useAppStore,
} from "../../../common";
import { useStyles } from "./weekdayPickerStyles";
import { ConfirmationDialog, ConfirmationDialogProps } from "../../shared";

export const WeekdayPicker = () => {
  const [appInsights, cart, context] = useAppStore((state) => [
    state.appInsights,
    state.cart,
    state.context,
  ]);
  const { value: currentCart, getCart, abandonCart } = cart;

  const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps>();
  const styles = useStyles();
  const selectId = useId();
  useEffect(() => {
    getCart();
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "WeekdayPicker",
      selectId,
    });
  }, [appInsights, context, selectId, getCart]);

  const setSelectedDate = useAppStore((state) => state.setSelectedDate);
  const setLoading = useAppStore((state) => state.setLoading);
  const selectedDate = useAppStore((state) => state.selectedDate);
  const today = dayjs();

  const getDisplayDate = useCallback(
    (day: Dayjs) => {
      return day === today
        ? "Today, " + dayjs(day).format("MMM D, YYYY")
        : dayjs(day).format("ddd, MMM D, YYYY");
    },
    [today],
  );
  const renderOptions = useCallback(
    (options: dayjs.Dayjs[]) =>
      options.map((day) => {
        return (
          <option
            key={dayjs(day).format("MM-DD-YYYY")}
            data-testid="weekdayPickerOption"
            aria-selected={day.isSame(selectedDate, "day")}
          >
            {day === today
              ? "Today, " + dayjs(day).format("MMM D, YYYY")
              : dayjs(day).format("ddd, MMM D, YYYY")}
          </option>
        );
      }),
    [today],
  );

  // Creates the dropdown options for the next seven consecutive days.
  const dateOptions = () => {
    const options = [today];
    const dayOfWeekNumber = today.day();
    for (let i = 1; i <= 7 - dayOfWeekNumber; i++) {
      options.push(today.add(i, "day"));
    }
    return options;
  };

  const onSelect = useCallback(
    (day: Dayjs) => {
      if (currentCart && currentCart.items) {
        setDialogProps({
          content: abandonCartDialogContent,
          open: true,
          title: abandonCartDialogTitle,
          confirmAction: async () => {
            trackEvent(
              appInsights,
              appInsightsEvents.Usage.Actions.AbandonCartDialog.DatePickerChangeConfirmed,
              context,
              {
                cafeId: currentCart?.cafeId,
                cartId: currentCart?.id,
                cafeName: currentCart?.cafeName,
                component: "WeekdayPicker",
              },
            );
            await abandonCart();
            setNewDate(day);
            setDialogProps(undefined);
          },
          dismissAction: () => {
            trackEvent(
              appInsights,
              appInsightsEvents.Usage.Actions.AbandonCartDialog.CafeChangeDismissed,
              context,
              {
                cafeId: currentCart?.cafeId,
                cartId: currentCart?.id,
                cafeName: currentCart?.cafeName,
                component: "WeekdayPicker",
              },
            );
            setDialogProps(undefined);
          },
        });
      } else {
        setNewDate(day);
      }
    },
    [setDialogProps, abandonCart, appInsights, context, currentCart],
  );

  const setNewDate = useCallback(
    async (day: Dayjs) => {
      setSelectedDate(day);
      setLoading(true);
      trackEvent(appInsights, appInsightsEvents.Usage.Actions.CalendarDaySelected, context, {
        selectedDate: day.format("MM-DD-YYYY"),
      });
    },
    [context, appInsights],
  );
  const ariaLabel = `Date, ${getDisplayDate(selectedDate)}. Tap to select date.`;

  return (
    <>
      {dialogProps && <ConfirmationDialog {...dialogProps} />}
      <header className={styles.container}>
        <Select
          icon={<CalendarLtr20Regular className={styles.icon} />}
          id={selectId}
          onChange={(e, data) => onSelect(dayjs(data.value))}
          value={getDisplayDate(selectedDate)}
          data-testid="weekdayPicker"
          aria-label={ariaLabel}
        >
          {renderOptions(dateOptions())}
        </Select>
      </header>
    </>
  );
};
